import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Send, Briefcase, CheckCircle, Users, Award, Target } from 'lucide-react';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Jobs = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    experience: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/apply`, formData);
      toast({
        title: "Bewerbung gesendet!",
        description: "Vielen Dank für Ihre Bewerbung. Wir melden uns in Kürze bei Ihnen.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        experience: '',
        message: ''
      });
    } catch (error) {
      console.error('Application form error:', error);
      toast({
        title: "Fehler beim Senden",
        description: "Die Bewerbung konnte nicht gesendet werden. Bitte versuchen Sie es später erneut oder kontaktieren Sie uns direkt per E-Mail.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const navigateHome = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <img 
              src="/images/davinci-logo-new.png" 
              alt="Da Vinci Institut" 
              className="h-16 md:h-24 cursor-pointer"
              onClick={navigateHome}
            />
            <Button
              onClick={navigateHome}
              variant="outline"
              className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 rounded-full"
            >
              Zurück zur Startseite
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section with Wave Background */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-teal-50 via-white to-teal-50/30 relative">
        {/* Decorative Background Shapes */}
        <div className="absolute top-20 left-0 w-72 h-72 bg-teal-100/40 rounded-full blur-3xl -translate-x-1/2"></div>
        <div className="absolute top-32 right-0 w-96 h-96 bg-teal-200/30 rounded-full blur-3xl translate-x-1/3"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block mb-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-teal-100 text-teal-800 text-sm font-medium">
                <Briefcase className="w-4 h-4 mr-2" />
                Karriere beim Da Vinci Institut
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Werden Sie Teil unseres{' '}
              <span className="bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent">
                professionellen Teams
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Wir suchen engagierte examinierte Lehrkräfte, die mit Leidenschaft und Expertise 
              Schüler auf ihrem Bildungsweg begleiten möchten.
            </p>
          </div>

          {/* Benefits Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-2 border-teal-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 rounded-3xl">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Users className="w-7 h-7 text-teal-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Tolles Team</h3>
                <p className="text-gray-600">Arbeiten Sie in einem motivierten und unterstützenden Team</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-teal-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 rounded-3xl">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Award className="w-7 h-7 text-teal-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Flexible Zeiten</h3>
                <p className="text-gray-600">Gestalten Sie Ihre Arbeitszeiten flexibel nach Ihren Bedürfnissen</p>
              </CardContent>
            </Card>

            <Card className="border-2 border-teal-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 rounded-3xl">
              <CardContent className="p-6 text-center">
                <div className="w-14 h-14 bg-teal-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Target className="w-7 h-7 text-teal-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Faire Vergütung</h3>
                <p className="text-gray-600">Attraktive Bezahlung für Ihre wertvolle Arbeit</p>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Wave Bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 50C240 80 480 100 720 80C960 60 1200 30 1440 50V100H0V50Z" fill="white"/>
          </svg>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-gray-200 shadow-lg rounded-3xl overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">Was wir erwarten</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Erfahrung im Unterrichten (Nachhilfe oder Schule)',
                  'Fachkompetenz in mindestens einem Hauptfach',
                  'Pädagogisches Geschick und Geduld',
                  'Freude am Umgang mit Kindern und Jugendlichen',
                  'Zuverlässigkeit und Engagement'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center mt-0.5">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-teal-50 via-white to-teal-50/30 relative">
        {/* Wave Top */}
        <div className="absolute top-0 left-0 right-0 -translate-y-1">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 0C240 60 480 80 720 60C960 40 1200 10 1440 30V0H0Z" fill="white"/>
          </svg>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Card className="border-gray-200 shadow-xl rounded-3xl overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                Jetzt als Dozent bewerben
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="name">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Ihr Name"
                      required
                      className="transition-all duration-200 focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">E-Mail *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="ihre.email@beispiel.de"
                      required
                      className="transition-all duration-200 focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefon *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+49 123 456 7890"
                      required
                      className="transition-all duration-200 focus:ring-2 focus:ring-teal-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Fachgebiet(e) *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder="z.B. Mathematik, Deutsch"
                      required
                      className="transition-all duration-200 focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="experience">Berufserfahrung *</Label>
                  <Input
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    placeholder="Jahre Erfahrung im Unterrichten"
                    required
                    className="transition-all duration-200 focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Ihre Motivation *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Erzählen Sie uns, warum Sie Teil unseres Teams werden möchten und welche Qualifikationen Sie mitbringen."
                    rows={5}
                    required
                    className="transition-all duration-200 focus:ring-2 focus:ring-teal-500 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full md:w-auto bg-teal-600 hover:bg-teal-700 text-white transition-all duration-300 hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed rounded-full px-8"
                >
                  {isSubmitting ? (
                    'Wird gesendet...'
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Bewerbung senden
                    </>
                  )}
                </Button>

                <p className="text-sm text-gray-600 mt-4">
                  * Pflichtfelder. Ihre Daten werden vertraulich behandelt.
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer with Large Logo */}
      <Footer />
    </div>
  );
};

export default Jobs;
